// Maps URL resource segments to display labels
export const KIND_LABELS: Record<string, string> = {
  workload: "Workload",
  identity: "Identity",
  volumeset: "Volume Set",
  gvc: "GVC",
  mk8s: "mk8s",
  agent: "Agent",
  secret: "Secret",
  cloudaccount: "Cloud Account",
  domain: "Domain",
  ipset: "IP Set",
  policy: "Policy",
  serviceaccount: "Service Account",
  group: "Group",
  location: "Location",
  auditctx: "Audit Context",
  quota: "Quota",
  repository: "Repository",
  image: "Image",
  user: "User",
  org: "Org",
  helmrelease: "Helm Release",
  logs: "Logs",
  audittrail: "Audit Trail",
  usage: "Cost & Usage",
};

export const KIND_LABELS_PLURAL: Record<string, string> = {
  workload: "Workloads",
  identity: "Identities",
  volumeset: "Volume Sets",
  gvc: "GVCs",
  mk8s: "mk8s",
  agent: "Agents",
  secret: "Secrets",
  cloudaccount: "Cloud Accounts",
  domain: "Domains",
  ipset: "IP Sets",
  policy: "Policies",
  serviceaccount: "Service Accounts",
  group: "Groups",
  location: "Locations",
  auditctx: "Audit Contexts",
  quota: "Quotas",
  repository: "Repositories",
  image: "Images",
  user: "Users",
  org: "Orgs",
  helmrelease: "Helm Releases",
  logs: "Logs",
  audittrail: "Audit Trail",
  usage: "Cost & Usage",
};

export interface TitleInfo {
  mode: "list" | "create" | "detail" | "draft" | "simple" | "invite";
  kind: string;
  kindPlural: string;
  itemName?: string;
}

// Simple page titles for non-resource pages
const SIMPLE_PAGE_TITLES: Record<string, string> = {
  "": "Control Plane", // Root path
  signin: "Sign In",
  signup: "Sign Up",
  task: "Tasks",
  billing: "Billing",
  credits: "Credits",
  redirect: "Control Plane",
};


/**
 * Pure function to parse URL and determine title info.
 * Exported separately for unit testing.
 */
export function parseTitleFromUrl(pathname: string): TitleInfo | null {
  // URL patterns:
  // /console/org/:org/:resource                    -> list
  // /console/org/:org/:resource/-create            -> create
  // /console/org/:org/:resource/:name              -> detail
  // /console/org/:org/:resource/-draft             -> draft
  // /console/org/:org/gvc/:gvc/:resource           -> list (gvc-scoped)
  // /console/org/:org/gvc/:gvc/:resource/-create   -> create (gvc-scoped)
  // /console/org/:org/gvc/:gvc/:resource/:name     -> detail (gvc-scoped)

  const parts = pathname.split("/").filter(Boolean);
  // parts: ["console", "org", "myorg", "workload", "myapp"]

  // Check for simple pages first (signin, task, root, etc.)
  const firstPart = parts[0] ?? "";
  if (firstPart in SIMPLE_PAGE_TITLES) {
    const title = SIMPLE_PAGE_TITLES[firstPart];
    return { mode: "simple", kind: title, kindPlural: title };
  }

  // Handle /home/* routes
  if (firstPart === "home") {
    const homeSection = parts[1];
    if (!homeSection) {
      return { mode: "simple", kind: "Home", kindPlural: "Home" };
    }
    if (homeSection === "profile") {
      return { mode: "simple", kind: "Profile", kindPlural: "Profile" };
    }
    if (homeSection === "org") {
      if (parts[2] === "-create") {
        return { mode: "create", kind: "Org", kindPlural: "Orgs" };
      }
      return { mode: "list", kind: "Org", kindPlural: "Orgs" };
    }
    if (homeSection === "account") {
      if (parts[2] === "-create") {
        return { mode: "create", kind: "Account", kindPlural: "Accounts" };
      }
      if (parts[2]) {
        // Account detail - don't use UUID as itemName, use Redux state instead
        return { mode: "detail", kind: "Account", kindPlural: "Accounts" };
      }
      return { mode: "list", kind: "Account", kindPlural: "Accounts" };
    }
    return { mode: "simple", kind: "Home", kindPlural: "Home" };
  }

  if (parts.length < 3 || parts[0] !== "console" || parts[1] !== "org") {
    return null;
  }

  const orgName = parts[2];

  // Need at least 4 parts for resource pages
  if (parts.length < 4) {
    return null;
  }

  // Handle marketplace routes: /console/org/:org/marketplace/template|release/:name
  if (parts[3] === "marketplace") {
    const section = parts[4]; // template or release
    const itemName = parts[5];
    if (section === "template") {
      if (!itemName) {
        return { mode: "list", kind: "Template", kindPlural: "Templates" };
      }
      if (parts[6] === "install") {
        return { mode: "create", kind: "Template", kindPlural: "Templates", itemName };
      }
      return { mode: "detail", kind: "Template", kindPlural: "Templates", itemName };
    }
    if (section === "release") {
      if (!itemName) {
        return { mode: "list", kind: "Release", kindPlural: "Releases" };
      }
      // Handle legacy releases: /marketplace/release/legacy/:name
      const releaseName = itemName === "legacy" ? parts[6] : itemName;
      return { mode: "detail", kind: "Release", kindPlural: "Releases", itemName: releaseName };
    }
    return { mode: "list", kind: "Release", kindPlural: "Releases" };
  }

  // Find the resource type - could be at index 3 (org-scoped) or 5 (gvc-scoped)
  // GVC-scoped pattern: /console/org/:org/gvc/:gvcName/:resource
  // But NOT when parts[4] is -create/-draft (that's creating a GVC itself)
  let resourceIndex = 3;
  if (parts[3] === "gvc" && parts.length >= 6 && parts[4] && !parts[4].startsWith("-")) {
    resourceIndex = 5;
  }

  const resource = parts[resourceIndex];

  // If the segment is not a known resource kind, treat it as an org detail page
  // e.g., /console/org/hakan/summary -> hakan - Org
  if (!resource || !KIND_LABELS[resource]) {
    return { mode: "detail", kind: "Org", kindPlural: "Orgs", itemName: orgName };
  }

  const kind = KIND_LABELS[resource];
  const kindPlural = KIND_LABELS_PLURAL[resource];
  const nextPart = parts[resourceIndex + 1];

  // Determine mode based on what follows the resource
  if (!nextPart) {
    return { mode: "list", kind, kindPlural };
  }

  if (nextPart === "-create") {
    return { mode: "create", kind, kindPlural };
  }

  if (nextPart === "-draft") {
    return { mode: "draft", kind, kindPlural };
  }

  // Handle -index pattern (e.g., /user/-index/-list, /user/-index/-invite)
  if (nextPart === "-index") {
    const indexSubPage = parts[resourceIndex + 2];
    if (indexSubPage === "-invite") {
      return { mode: "invite", kind, kindPlural };
    }
    // -list or default
    return { mode: "list", kind, kindPlural };
  }

  // Handle legacy routes (e.g., /helmrelease/legacy/:name)
  if (nextPart === "legacy") {
    const legacyName = parts[resourceIndex + 2];
    return { mode: "detail", kind, kindPlural, itemName: legacyName };
  }

  // For quota detail, don't use the UUID as itemName - use Redux state instead
  if (resource === "quota") {
    return { mode: "detail", kind, kindPlural };
  }

  // Otherwise it's a detail page with item name
  return { mode: "detail", kind, kindPlural, itemName: nextPart };
}

/**
 * Build the final title string from TitleInfo.
 * Exported separately for unit testing.
 */
export function buildTitle(info: TitleInfo, reduxName?: string | null): string {
  if (info.mode === "simple") {
    return info.kind;
  }

  const parts: string[] = [];

  if (info.mode === "create") {
    parts.push("Create");
    parts.push(info.kind);
  } else if (info.mode === "invite") {
    parts.push("Invite");
    parts.push(info.kind);
  } else if (info.mode === "draft") {
    // Draft with name = editing existing item, without name = creating new item
    if (reduxName) {
      parts.push("Edit");
      parts.push(reduxName);
    } else {
      parts.push("Create");
    }
    parts.push(info.kind);
  } else if (info.mode === "detail") {
    // Use itemName from URL, or reduxName if itemName not available (e.g., account detail)
    if (info.itemName) {
      parts.push(info.itemName);
    } else if (reduxName) {
      parts.push(reduxName);
    }
    parts.push(info.kind);
  } else {
    // list
    parts.push(info.kindPlural);
  }

  return parts.join(" - ");
}
